import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { CreditCard, Sparkles, Loader2, ExternalLink, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSubscription, useStartCheckout, useOpenPortal, type SubscriptionStatus } from "@/hooks/useSubscription";
import { formatError } from "@/lib/utils";

const STATUS_META: Record<SubscriptionStatus, { label: string; tone: "positive" | "warning" | "negative" | "neutral"; icon: typeof CheckCircle2 }> = {
  none:               { label: "Not subscribed",   tone: "neutral",  icon: CreditCard },
  trialing:           { label: "Trial",            tone: "positive", icon: Sparkles },
  active:             { label: "Active",           tone: "positive", icon: CheckCircle2 },
  past_due:           { label: "Payment overdue",  tone: "warning",  icon: AlertCircle },
  cancelled:          { label: "Cancelled",        tone: "negative", icon: AlertCircle },
  incomplete:         { label: "Incomplete",       tone: "warning",  icon: Clock },
  incomplete_expired: { label: "Setup expired",    tone: "negative", icon: AlertCircle },
  unpaid:             { label: "Unpaid",           tone: "negative", icon: AlertCircle },
  paused:             { label: "Paused",           tone: "warning",  icon: Clock },
};

function StatusBadge({ status }: { status: SubscriptionStatus }) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  const toneClass =
    meta.tone === "positive" ? "text-green-500 border-green-500/30 bg-green-500/10" :
    meta.tone === "warning"  ? "text-yellow-500 border-yellow-500/30 bg-yellow-500/10" :
    meta.tone === "negative" ? "text-destructive border-destructive/30 bg-destructive/10" :
                               "text-muted-foreground";
  return (
    <Badge variant="outline" className={`${toneClass} text-[11px] flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
}

export function BillingSection() {
  const { data: sub, isLoading, refetch } = useSubscription();
  const startCheckout = useStartCheckout();
  const openPortal = useOpenPortal();
  const [discountCode, setDiscountCode] = useState("");
  const [showDiscount, setShowDiscount] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle ?checkout=success|cancelled from Stripe redirect
  useEffect(() => {
    const co = searchParams.get("checkout");
    if (!co) return;
    if (co === "success") {
      toast.success("Subscription active. Welcome to Hella Organized.");
      refetch();
    } else if (co === "cancelled") {
      toast.info("Checkout cancelled. No charge was made.");
    }
    searchParams.delete("checkout");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, refetch]);

  if (isLoading || !sub) {
    return (
      <section className="mb-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Billing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  const hasCustomer = !!sub.stripe_customer_id;
  // Subscribe primary action when no usable Stripe customer + status === none.
  // Once we have any Stripe customer record, Portal handles resubscribe too.
  const showSubscribe = !hasCustomer || sub.status === "none";

  const handleSubscribe = async () => {
    try {
      await startCheckout.mutateAsync({ discount_code: discountCode || undefined });
    } catch (err) {
      toast.error(`Checkout failed: ${formatError(err)}`);
    }
  };

  const handleManage = async () => {
    try {
      await openPortal.mutateAsync();
    } catch (err) {
      toast.error(`Portal failed: ${formatError(err)}`);
    }
  };

  const priceLabel =
    sub.stripe_price_id
      ? sub.stripe_price_id.toLowerCase().includes("earlybird") ? "$299 / month (Early Bird)" : "$1,000 / month"
      : null;

  const renewalLine =
    sub.current_period_end && (sub.status === "active" || sub.status === "trialing")
      ? sub.cancel_at_period_end
        ? `Ends on ${format(new Date(sub.current_period_end), "MMM d, yyyy")}`
        : `Renews on ${format(new Date(sub.current_period_end), "MMM d, yyyy")}`
      : null;

  return (
    <section className="mb-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Billing
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                Subscription, payment method, invoices.
              </CardDescription>
            </div>
            <StatusBadge status={sub.status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(priceLabel || renewalLine) && (
            <div className="text-xs space-y-1">
              {priceLabel && (
                <p>
                  <span className="text-muted-foreground">Plan:</span> {priceLabel}
                </p>
              )}
              {renewalLine && <p className="text-muted-foreground">{renewalLine}</p>}
            </div>
          )}

          {sub.status === "past_due" && (
            <div className="text-xs flex items-start gap-2 p-3 rounded-md bg-yellow-500/10 border border-yellow-500/20">
              <AlertCircle className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
              <div>
                Your last payment failed. Update your card in the Stripe portal to keep your subscription active.
              </div>
            </div>
          )}

          {showSubscribe ? (
            <div className="space-y-3">
              <div className="text-xs space-y-1">
                <p>
                  <span className="font-medium">Pro plan:</span> $1,000 / month.
                </p>
                <p className="text-muted-foreground">
                  Everything in HO — AI chat, Gmail / Plaud / Fireflies / GitHub / Linear / Jira sync, agent dispatch, live Claude Code sessions, push notifications.
                </p>
              </div>

              {!showDiscount ? (
                <button
                  type="button"
                  onClick={() => setShowDiscount(true)}
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                >
                  Have a discount code?
                </button>
              ) : (
                <div>
                  <Label className="text-xs">Discount code</Label>
                  <Input
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    placeholder="EARLYBIRD"
                    className="mt-1 font-mono text-xs uppercase"
                  />
                  {discountCode.toUpperCase() === "EARLYBIRD" && (
                    <p className="text-xs text-green-500 mt-1">
                      Early Bird applied — $299/mo instead of $1,000/mo.
                    </p>
                  )}
                </div>
              )}

              <Button onClick={handleSubscribe} disabled={startCheckout.isPending} className="w-full">
                {startCheckout.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Subscribe
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button onClick={handleManage} disabled={openPortal.isPending} variant="outline" className="flex-1">
                {openPortal.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ExternalLink className="h-4 w-4 mr-2" />
                )}
                Manage in Stripe
              </Button>
              {sub.status === "cancelled" && (
                <Button onClick={handleSubscribe} disabled={startCheckout.isPending}>
                  {startCheckout.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Resubscribe
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
